import { Fragment, FunctionalComponent, h } from "preact";
import { lazy, Suspense } from "preact/compat";
import Router, { route, Route, RouterOnChangeArgs } from "preact-router";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setAliasMode,
  setPortalAlias,
  setPortalId,
  setWorkspaceAlias,
  setWorkspaceId,
} from "../../redux/slices/aliasSlice";
import {
  setMode,
  setPreview,
  setNarrowFactor,
  setWsd,
} from "../../redux/slices/appearanceSlice";

import { setAzureSasToken } from "../../redux/slices/authSlice";

import { get_DASHBOARD_URI_BASE,getIsEdge } from "../../configs/env";
import { Utils, checkPublicUserId, getEdgeUserLoginSession, isEdgeUserSessionExpired, setTheme } from "../../utils";

import axios from "axios";
import logger from "../../logger";

import {
  setClientIp,
  setUserId,
  setUserIdRan,
  setCustomUserId,
} from "../../redux/slices/globalsSlice";
import { useState } from "preact/hooks";

// Lazy load all route components for code splitting
const Home = lazy(() => import("../../routes/home"));
const Search = lazy(() => import("../../routes/search"));
const Pdf = lazy(() => import("../../routes/pdf"));
const Video = lazy(() => import("../../routes/video"));
const Sheet = lazy(() => import("../../routes/sheet"));
const Notfound = lazy(() => import("../../routes/notfound"));
const CustomError = lazy(() => import("../../routes/customError"));
const Login = lazy(() => import("../../routes/login"));
const Chatbot = lazy(() => import("../../routes/chatbot"));
const KorraCopilot = lazy(() => import("../../routes/korra-copilot"));
const LoginPage = lazy(() => import("../../routes/edgeLoginPage"));
const Portal = lazy(() => import("../../routes/graph-portal"));
const KeywordSearch = lazy(() => import("../../routes/keywordSearch/keywordSearch"));
const KeywordLayout = lazy(() => import("../../routes/home/keywordLayout"));

// Simple loading spinner
const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div>Loading...</div>
  </div>
);

export interface RouterProps {
  workspaceId: string;
  kappId: string;
  portalId: string;
  workspaceAlias: string;
  portalAlias: string;

  // editor
  preview?: string;

  // search
  q: string;
  back: boolean;
  // pdf / video
  guid: string;

  // pdf
  docId: string; // docGuid
  page: string;

  // video
  docGuid: string;

  // widget
  mode: "widget";

  // narrowfactor
  narrowfactor: string;

  sasTokenPreview?: string;
  chatbotFlag?: boolean;
  userID?: string;
  
  // korra-copilot
  model?: string;
  problem?: string;
}
const AppRouter: FunctionalComponent = () => {
  const appearance = useAppSelector((state) => state.appearance);
  const globals = useAppSelector((state) => state.globals);
  const alias = useAppSelector((state) => state.alias);
  const auth = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [isEdge, setIsEdge] = useState<boolean|undefined>(getIsEdge());
   
  /**
   * error 400
   * @param routerProps
   */
  const route400 = (routerProps: RouterProps) => {
    logger.warn({
      message: "Something wrong with routerProps",
      router_props: routerProps,
      portal_alias: alias.portalAlias,
      workspace_id: alias.workspaceId
    });
    route(`/error/${400}/errorMessage/${"Bad URL."}`);
  };

  const route422 = (errorMessage: string) => {
    route(`/error/${422}/errorMessage/${errorMessage}`);
  };

  const isSasTokenExpired = (timestamp: number) => {
    const now = new Date().getTime();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 1000 / 60);
    return minutes > 30;
  };

  /**
   * on route change
   * @param args
   * @returns
   */
  const onChange = async (args: RouterOnChangeArgs) => {
    try {
      const matches = (args.current.props as any)?.matches;
      if (isEdge){
        const user = await getEdgeUserLoginSession();
        if (user && !isEdgeUserSessionExpired(user?.timestamp)) {
          if (user.workspaceId !== matches?.portalAlias) {
            route("/edge/login", true);
          }
          logger.log({
            message: "App router changed",
            user: user,
            portal_alias: alias.portalAlias,
            workspace_id: user?.workspaceId,
          });
        } else {
          route("/edge/login", true);
        }
      }
      if (args.path?.includes("/login")) return;
      if (matches) {
        const routerProps = {
          workspaceId: matches?.workspaceId,
          kappId: matches?.kappId,
          portalId: matches?.portalId,
          workspaceAlias: (args.current.props as any)?.matches?.workspaceAlias,
          portalAlias: matches?.portalAlias,
          preview: matches?.preview,
          q: matches?.q,
          back: matches?.back,
          guid: matches?.guid,
          docId: matches?.docId,
          page: matches?.page,
          docGuid: matches?.docGuid,
          mode: matches?.mode,
          narrowfactor: matches?.narrowfactor,
          sasTokenPreview: matches?.sasTokenPreview,
          chatbotFlag: args.path?.includes("/chatbot"),
          userID: matches?.userID,
          model: matches?.model,
          problem: matches?.problem,
        } as RouterProps;
        if (routerProps.preview === "preview") {
          dispatch(setPreview(true));
          if (routerProps.sasTokenPreview) {
            dispatch(
              setAzureSasToken(decodeURIComponent(routerProps.sasTokenPreview))
            );
          }
        }

        if (appearance.loaded && !appearance.isPrivate && !globals.userIdRan) {
          const userID = checkPublicUserId();
          dispatch(setUserId(userID));

          //to make user we dont run the useEffect more than once just when appearance.loaded === true
          dispatch(setUserIdRan(true));
        }

        if (!appearance.narrowfactor && routerProps.narrowfactor == "1") {
          dispatch(setNarrowFactor(1));
        }

        if (routerProps.mode && routerProps.mode !== appearance.mode)
          dispatch(setMode(routerProps.mode));

        const urlParams = new URLSearchParams(window.location.search);
        const wsdFromUrl = urlParams.get("wsd") === "true";
        if (wsdFromUrl !== appearance.wsd) {
          dispatch(setWsd(wsdFromUrl));
        }

        if (routerProps.userID) {
          localStorage.setItem("korraCustomUserId", routerProps.userID);
          dispatch(setCustomUserId(routerProps.userID));
        }

        if (!globals.clientIp && !isEdge) {
          try {
            const _clientIp = await axios.get(
              "https://api.ipify.org?format=json"
            );
            if (_clientIp?.data?.ip) {
              dispatch(setClientIp(_clientIp.data.ip));
            }
          } catch (error) {
            logger.error({
              message: "Failed to get client IP",
              error: error,
              stacktrace: (error as any)?.stack,
              portal_alias: alias.portalAlias,
              workspace_id: alias.workspaceId
            });
          }
        }

        if (
          routerProps.workspaceAlias &&
          routerProps.portalAlias &&
          !alias.workspaceId &&
          !alias.portalId
        ) {
          const debugg = get_DASHBOARD_URI_BASE();
          const url = `${get_DASHBOARD_URI_BASE()}/alias/${
            routerProps.workspaceAlias
          }/${routerProps.portalAlias}`;
          logger.log({
            message: "Alias lookup URLs",
            alias_url: url,
            workspace_alias: routerProps.workspaceAlias,
            portal_alias: routerProps.portalAlias
          });
          const _aliasResponce = await axios(
            `${get_DASHBOARD_URI_BASE()}/alias/${routerProps.workspaceAlias}/${
              routerProps.portalAlias
            }`
          );
          const aliasResponse = _aliasResponce?.data?.data;

          if (aliasResponse?.workspaceId && aliasResponse?.portalId) {
            // Get the portal details with themes
            const portalResponse = await axios(
              `${get_DASHBOARD_URI_BASE()}/portals/${
                aliasResponse.portalId
              }/workspaces/${aliasResponse.workspaceId}`
            );
            const portalData = portalResponse?.data?.data;

            dispatch(setWorkspaceId(aliasResponse.workspaceId));
            dispatch(setPortalId(aliasResponse.portalId));
            dispatch(setWorkspaceAlias(routerProps.workspaceAlias));
            dispatch(setPortalAlias(routerProps.portalAlias));
            dispatch(setAliasMode(portalData.mode));

            // Get the correct theme based on mode
            let selectedTheme;
            if (portalData.mode === "chatbot") {
              selectedTheme = portalData.chatThemes.find(
                (t: any) => t?.name === "publish"
              );
            } else {
              selectedTheme = portalData.themes.find(
                (t: any) => t?.name === "publish"
              );
            }

            if (selectedTheme) {
              setTheme(selectedTheme, dispatch, aliasResponse.workspaceId);
            }

            const isPrivate =
              portalData.mode === "chatbot"
                ? selectedTheme?.isPrivate
                : selectedTheme?.isPrivate;

            if (
              (portalData.mode === "chatbot" &&
                isPrivate &&
                routerProps.mode !== "widget") ||
              isPrivate
            ) {
              if (routerProps.preview === "preview") {
                dispatch(setPreview(true));
                return;
              }

              if (!auth.korraUser && !auth.auth0User && !isEdge) {
                Utils.routeToLogin({
                  workspaceAlias: routerProps.workspaceAlias,
                  portalAlias: routerProps.portalAlias,
                });
              }
            }
            if (!isPrivate && !globals.userIdRan) {
              const userID = checkPublicUserId();
              dispatch(setUserId(userID));

              dispatch(setUserIdRan(true));
            }
          } else {
            route422("It is alias but couldn't get alias response");
          }
        } else if (
          routerProps.workspaceId &&
          (routerProps.portalId || routerProps.kappId)
        ) {
          dispatch(setWorkspaceId(routerProps.workspaceId));
          if (routerProps.portalId) {
            dispatch(setPortalId(routerProps.portalId));

            const theme = await Utils.getTheme(
              routerProps.workspaceId,
              routerProps.portalId,
              !routerProps.mode
            );

            if (theme) {
              setTheme(theme, dispatch, routerProps.workspaceId);
            }
            if (theme?.isPrivate) {
              if (routerProps.preview === "preview") {
                dispatch(setPreview(true));
                return;
              }
              if (!auth.korraUser && !auth.auth0User && !isEdge) {
                Utils.routeToLogin(null);
              }
            }
            if (!theme?.isPrivate && !globals.userIdRan) {
              const userID = checkPublicUserId();
              dispatch(setUserId(userID));

              //to make user we dont run the useEffect more than once just when appearance.loaded === true
              dispatch(setUserIdRan(true));
            }
          } else if (routerProps.kappId) {
            // dispatch(setKappId(routerProps.kappId));
            // no need theme, no need alias, all is ok, it is kapp
          } else {
            route400(routerProps);
          }
        } else {
          if (
            appearance.isPrivate &&
            (!alias.workspaceAlias || !alias.workspaceId)
          ) {
            if (routerProps.preview === "preview") {
              dispatch(setPreview(true));
              return;
            }
            route400(routerProps);
          }
        }
      } else {
        route422("No matches at URL params");
      }
    } catch (error: any) {
      logger.error({
        message: "Error in router onChange handler",
        error: error,
        stacktrace: error?.stack,
        portal_alias: alias.portalAlias,
        workspace_id: alias.workspaceId,
        path: args.path,
        error_detail: error?.message || ""
      });
      route422("Something wrong: " + error?.message || "");
    }
  };

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Router onChange={onChange}>
        <Route path="/workspace/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/chatbot"
          component={Chatbot}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />

        <Route
          path="/workspace/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        <Route path="/company/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/company/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route path="/company/:workspaceId/portal/:portalId" component={Home} />
        <Route
          path="/company/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/graph-portal"
          component={Portal}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/graph-portal"
          component={Portal}
        />

        {/*  */}

        <Route path="/:workspaceAlias/:portalAlias" component={Home} />
        <Route path="/:workspaceAlias/:portalAlias/home" component={Home} />
        <Route
          path="/:workspaceAlias/:portalAlias/keywordHome"
          component={KeywordLayout}
        />
        <Route path="/:workspaceAlias/:portalAlias/search" component={Search} />
        <Route
          path="/:workspaceAlias/:portalAlias/keywordSearch"
          component={KeywordSearch}
        />
        <Route path="/:workspaceAlias/:portalAlias/login" component={Login} />
        <Route
          path="/:workspaceAlias/:portalAlias/chatbot"
          component={Chatbot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/korra-copilot?mode=widget"
          component={KorraCopilot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/chatbot?mode=widget"
          component={Chatbot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Video}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Pdf}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Sheet}
        />

        {/*  */}

        <Route
          path="/error/:statusCode/errorMessage/:errorMessage"
          component={CustomError}
        />

        {isEdge ? <LoginPage default/> :<Notfound default />}

        {/*  */}
        <Route
          path="/:workspaceAlias/:portalAlias/login/redirect/:redirect"
          component={Login}
        />
        <Route path="/login/redirect/:redirect" component={Login} />
        <Route path="/login" component={Login} />

        {/* edge routes */}
        <Route path="/edge/login" component={LoginPage} />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/chatbot"
          component={Chatbot}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />

        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        <Route path="/app/company/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        {/*  */}

        <Route path="/app/:workspaceAlias/:portalAlias" component={Home} />
        <Route path="/app/:workspaceAlias/:portalAlias/home" component={Home} />
        <Route
          path="/app/:workspaceAlias/:portalAlias/search"
          component={Search}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/keywordSearch"
          component={KeywordSearch}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/login"
          component={Login}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/chatbot"
          component={Chatbot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/korra-copilot?mode=widget"
          component={KorraCopilot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/chatbot?mode=widget"
          component={Chatbot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Video}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Pdf}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Sheet}
        />

        {/*  */}

        <Route
          path="/app/error/:statusCode/errorMessage/:errorMessage"
          component={CustomError}
        />

        {/*  */}
        <Route
          path="/app/:workspaceAlias/:portalAlias/login/redirect/:redirect"
          component={Login}
        />
        <Route path="/app/login/redirect/:redirect" component={Login} />
        <Route path="/app/login" component={Login} />

        {/* private */}
      </Router>
    </Suspense>
  );
};

export default AppRouter;
